// Home Sections Service - Realtime Database operations
import { ref, get, update, remove, set, push, onValue, off } from 'firebase/database'
import { database } from './firebase'
import {
  HIGH_IMPACT_DELETIONS_DISABLED,
  getHighImpactDeletionBlock,
} from '@/utils/highImpactDeletionGuard'
import {
  parseAssignedProducts as parseSectionProducts,
  sortAssignedProducts as sortSectionProducts,
  getNextProductOrder,
} from '@/utils/products/assignedProductsHelpers'

// Base path for home sections in Firebase Realtime Database
const HOME_SECTIONS_BASE_PATH = 'projects/superkomprasBackoffice/homeSections'

// Helper para obtener timestamp en milisegundos
const getTimestamp = () => Date.now()

/**
 * Get all home sections from Realtime Database, sorted by order
 * @returns {Promise<Object>} All sections data sorted by order
 */
export const getHomeSections = async () => {
  try {
    const sectionsRef = ref(database, HOME_SECTIONS_BASE_PATH)
    const snapshot = await get(sectionsRef)

    if (snapshot.exists()) {
      const sectionsData = snapshot.val()

      // Convert object to array with id as key, sorted by order
      const sectionsArray = Object.keys(sectionsData)
        .map(id => ({
          id,
          nombre: sectionsData[id].nombre || '',
          orden: sectionsData[id].orden || 0,
          activa: sectionsData[id].activa !== undefined ? sectionsData[id].activa : true,
          createdAt: sectionsData[id].createdAt || null,
          updatedAt: sectionsData[id].updatedAt || null,
          productos: sectionsData[id].productos || {},
        }))
        .sort((a, b) => (a.orden || 0) - (b.orden || 0))

      return {
        success: true,
        data: sectionsArray,
      }
    } else {
      return {
        success: true,
        data: [],
      }
    }
  } catch (error) {
    console.error('Error in getHomeSections:', error)
    return {
      success: false,
      error: error.message || 'Error al obtener secciones de home',
      data: [],
    }
  }
}

/**
 * Get a single home section by ID
 * @param {string} id - Section ID
 * @returns {Promise<Object>} Section data or error
 */
export const getSectionById = async id => {
  try {
    if (!id) {
      return {
        success: false,
        error: 'El ID de la sección es requerido',
      }
    }

    const sectionRef = ref(database, `${HOME_SECTIONS_BASE_PATH}/${id}`)
    const snapshot = await get(sectionRef)

    if (!snapshot.exists()) {
      return {
        success: false,
        error: 'La sección no existe',
      }
    }

    const sectionData = snapshot.val()

    return {
      success: true,
      data: {
        id: id,
        ...sectionData,
      },
    }
  } catch (error) {
    console.error('Error in getSectionById:', error)
    return {
      success: false,
      error: error.message || 'Error al obtener la sección',
    }
  }
}

/**
 * Create a new home section in Realtime Database
 * @param {Object} payload - Section data { nombre, orden, activa }
 * @returns {Promise<Object>} Result of create operation
 */
export const createHomeSection = async payload => {
  try {
    // Validate required fields
    if (!payload.nombre || payload.nombre.trim() === '') {
      return {
        success: false,
        error: 'El nombre de la sección es requerido',
      }
    }

    const timestamp = getTimestamp()

    // Prepare section data
    const sectionData = {
      nombre: payload.nombre.trim(),
      orden: payload.orden !== undefined ? payload.orden : 0,
      activa: payload.activa !== undefined ? payload.activa : true,
      createdAt: timestamp,
      updatedAt: timestamp,
      productos: {},
    }

    // Create new section with push (generates unique ID)
    const sectionsRef = ref(database, HOME_SECTIONS_BASE_PATH)
    const newSectionRef = push(sectionsRef)
    await set(newSectionRef, sectionData)

    return {
      success: true,
      message: 'Sección creada correctamente',
      id: newSectionRef.key,
      data: {
        id: newSectionRef.key,
        ...sectionData,
      },
    }
  } catch (error) {
    console.error('Error in createHomeSection:', error)
    return {
      success: false,
      error: error.message || 'Error al crear sección de home',
    }
  }
}

/**
 * Update a home section in Realtime Database
 * @param {string} id - Section ID
 * @param {Object} payload - Fields to update { nombre?, orden?, activa? }
 * @returns {Promise<Object>} Result of update operation
 */
export const updateHomeSection = async (id, payload) => {
  try {
    if (!id) {
      return {
        success: false,
        error: 'El ID de la sección es requerido',
      }
    }

    const sectionRef = ref(database, `${HOME_SECTIONS_BASE_PATH}/${id}`)

    // Check if section exists
    const snapshot = await get(sectionRef)
    if (!snapshot.exists()) {
      return {
        success: false,
        error: 'La sección no existe',
      }
    }

    const timestamp = getTimestamp()

    // Prepare update data (only include fields that are provided)
    const updateData = {
      updatedAt: timestamp,
    }

    if (payload.nombre !== undefined) {
      updateData.nombre = payload.nombre.trim()
    }

    if (payload.orden !== undefined) {
      updateData.orden = payload.orden
    }

    if (payload.activa !== undefined) {
      updateData.activa = payload.activa
    }

    // Update section
    await update(sectionRef, updateData)

    return {
      success: true,
      message: 'Sección actualizada correctamente',
    }
  } catch (error) {
    console.error('Error in updateHomeSection:', error)
    return {
      success: false,
      error: error.message || 'Error al actualizar sección de home',
    }
  }
}

/**
 * Delete a home section from Realtime Database
 * @param {string} id - Section ID to delete
 * @returns {Promise<Object>} Result of delete operation
 */
export const deleteHomeSection = async id => {
  try {
    if (HIGH_IMPACT_DELETIONS_DISABLED) {
      return getHighImpactDeletionBlock('eliminar sección de home')
    }

    if (!id) {
      return {
        success: false,
        error: 'El ID de la sección es requerido',
      }
    }

    const sectionRef = ref(database, `${HOME_SECTIONS_BASE_PATH}/${id}`)

    // Check if section exists
    const snapshot = await get(sectionRef)
    if (!snapshot.exists()) {
      return {
        success: false,
        error: 'La sección no existe',
      }
    }

    // Delete section (this will also delete all products inside)
    await remove(sectionRef)

    return {
      success: true,
      message: 'Sección eliminada correctamente',
    }
  } catch (error) {
    console.error('Error in deleteHomeSection:', error)
    return {
      success: false,
      error: error.message || 'Error al eliminar sección de home',
    }
  }
}

/**
 * Add a product to a section
 * @param {string} sectionId - Section ID
 * @param {Object} productObject - Product object from Algolia response
 * @returns {Promise<Object>} Result of add operation
 */
export const addProductToSection = async (sectionId, productObject) => {
  try {
    if (!sectionId) {
      return {
        success: false,
        error: 'El ID de la sección es requerido',
      }
    }

    if (!productObject || !productObject.ProductoID) {
      return {
        success: false,
        error: 'El objeto del producto debe tener un ProductoID',
      }
    }

    const sectionRef = ref(database, `${HOME_SECTIONS_BASE_PATH}/${sectionId}`)

    // Check if section exists
    const snapshot = await get(sectionRef)
    if (!snapshot.exists()) {
      return {
        success: false,
        error: 'La sección no existe',
      }
    }

    const productId = productObject.ProductoID.toString()
    const productRef = ref(
      database,
      `${HOME_SECTIONS_BASE_PATH}/${sectionId}/productos/${productId}`
    )

    // Check if product already exists in section
    const productSnapshot = await get(productRef)
    if (productSnapshot.exists()) {
      return {
        success: false,
        error: 'El producto ya existe en esta sección',
      }
    }

    const timestamp = getTimestamp()
    const sectionData = snapshot.val()
    const nextOrder = getNextProductOrder(sectionData.productos || {})

    // Prepare product data with the required shape
    const productData = {
      Data: productObject,
      order: nextOrder,
      ProductoID: productId,
      addedAt: timestamp,
    }

    // Add product to section
    await set(productRef, productData)

    // Update section's updatedAt
    await update(sectionRef, {
      updatedAt: timestamp,
    })

    return {
      success: true,
      message: 'Producto agregado a la sección correctamente',
      productId: productId,
    }
  } catch (error) {
    console.error('Error in addProductToSection:', error)
    return {
      success: false,
      error: error.message || 'Error al agregar producto a la sección',
    }
  }
}

/**
 * Reorder section products (massive update)
 * @param {string} sectionId - Section ID
 * @param {Array} products - Array of products with new order (must include ProductoID)
 * @returns {Promise<Object>} Result of update operation
 */
export const reorderSectionProducts = async (sectionId, products) => {
  try {
    if (!sectionId) {
      return {
        success: false,
        error: 'El ID de la sección es requerido',
      }
    }

    if (!products || !Array.isArray(products) || products.length === 0) {
      return {
        success: true,
        message: 'No hay productos para reordenar',
      }
    }

    const timestamp = getTimestamp()
    const updates = {}

    products.forEach((product, index) => {
      const productId = (product.ProductoID || product.productoId)?.toString()
      if (!productId) return

      updates[`${HOME_SECTIONS_BASE_PATH}/${sectionId}/productos/${productId}/order`] = index + 1
    })

    updates[`${HOME_SECTIONS_BASE_PATH}/${sectionId}/updatedAt`] = timestamp

    const rootRef = ref(database)
    await update(rootRef, updates)

    return {
      success: true,
      message: 'Productos reordenados correctamente',
    }
  } catch (error) {
    console.error('Error in reorderSectionProducts:', error)
    return {
      success: false,
      error: error.message || 'Error al reordenar productos de la sección',
    }
  }
}

/**
 * Remove a product from a section
 * @param {string} sectionId - Section ID
 * @param {string} productId - Product ID to remove
 * @returns {Promise<Object>} Result of remove operation
 */
export const removeProductFromSection = async (sectionId, productId) => {
  try {
    if (!sectionId) {
      return {
        success: false,
        error: 'El ID de la sección es requerido',
      }
    }

    if (!productId) {
      return {
        success: false,
        error: 'El ID del producto es requerido',
      }
    }

    const productRef = ref(
      database,
      `${HOME_SECTIONS_BASE_PATH}/${sectionId}/productos/${productId}`
    )

    // Check if product exists in section
    const snapshot = await get(productRef)
    if (!snapshot.exists()) {
      return {
        success: false,
        error: 'El producto no existe en esta sección',
      }
    }

    // Remove product from section
    await remove(productRef)

    const sectionRef = ref(database, `${HOME_SECTIONS_BASE_PATH}/${sectionId}`)
    const sectionSnapshot = await get(sectionRef)
    const remainingProductos = sectionSnapshot.exists() ? sectionSnapshot.val().productos || {} : {}

    const remainingProducts = sortSectionProducts(parseSectionProducts(remainingProductos))

    if (remainingProducts.length > 0) {
      const reorderResult = await reorderSectionProducts(sectionId, remainingProducts)
      if (!reorderResult.success) {
        return reorderResult
      }
    } else {
      await update(sectionRef, {
        updatedAt: getTimestamp(),
      })
    }

    return {
      success: true,
      message: 'Producto eliminado de la sección correctamente',
    }
  } catch (error) {
    console.error('Error in removeProductFromSection:', error)
    return {
      success: false,
      error: error.message || 'Error al eliminar producto de la sección',
    }
  }
}

/**
 * Listen to home sections changes in real-time
 * @param {Function} callback - Callback function to execute on data change
 * @returns {Function} Unsubscribe function
 */
export const subscribeToHomeSections = callback => {
  const sectionsRef = ref(database, HOME_SECTIONS_BASE_PATH)

  const unsubscribe = onValue(
    sectionsRef,
    snapshot => {
      if (snapshot.exists()) {
        const sectionsData = snapshot.val()

        // Convert object to array with id as key, sorted by order
        const sectionsArray = Object.keys(sectionsData)
          .map(id => ({
            id,
            nombre: sectionsData[id].nombre || '',
            orden: sectionsData[id].orden || 0,
            activa: sectionsData[id].activa !== undefined ? sectionsData[id].activa : true,
            createdAt: sectionsData[id].createdAt || null,
            updatedAt: sectionsData[id].updatedAt || null,
            productos: sectionsData[id].productos || {},
          }))
          .sort((a, b) => (a.orden || 0) - (b.orden || 0))

        callback({ success: true, data: sectionsArray })
      } else {
        callback({ success: true, data: [] })
      }
    },
    error => {
      console.error('Error in subscribeToHomeSections:', error)
      callback({
        success: false,
        error: error.message || 'Error al obtener secciones de home',
        data: [],
      })
    }
  )

  // Return unsubscribe function
  return () => off(sectionsRef)
}
